import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  User,
  Shield,
  Mail,
  Phone,
  Building,
  Key,
  AlertTriangle,
  CheckCircle,
  UserCircle,
  Briefcase,
  Calendar,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Building2,
  Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { authAPI } from "@/services/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OrganizationSettings from "@/pages/OrganizationSettings";

export default function Settings() {
  const { user, company } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);



  const handleDeleteAccount = () => {
    toast({
      title: "Solicitud recibida",
      description: "Tu cuenta será eliminada en 30 días",
      variant: "destructive",
    });
    setIsDeleteOpen(false);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <User className="h-12 w-12 text-gray-300 mx-auto" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay usuario
            </h3>
            <p className="text-gray-600">
              Inicia sesión para ver la configuración
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col p-8 bg-white rounded-[32px] shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] border border-slate-200/60">
      <div className="w-full space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-slate-200 p-2.5 rounded-2xl">
                <SettingsIcon className="h-7 w-7 text-slate-700" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Configuración
              </h1>
            </div>
            <p className="text-slate-500 text-sm md:text-base ml-1">
              Gestiona tu perfil y las preferencias de cuenta
            </p>
          </div>
        </div>

        <Tabs defaultValue="perfil" className="w-full">
          <TabsList className="flex w-fit bg-slate-100/80 p-1.5 rounded-2xl mb-8">
            <TabsTrigger 
              value="perfil" 
              className="flex items-center gap-2 rounded-xl px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-indigo-600 font-medium transition-all"
            >
              <UserCircle className="w-4 h-4" />
              Mi Perfil
            </TabsTrigger>
            {user?.role?.name?.toLowerCase() === "admin" || user?.role?.name?.toLowerCase() === "super admin" ? (
              <TabsTrigger 
                value="empresa" 
                className="flex items-center gap-2 rounded-xl px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-indigo-600 font-medium transition-all"
              >
                <Building2 className="w-4 h-4" />
                Mi Empresa
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="perfil" className="mt-0 border-none p-0 outline-none animate-in fade-in-50 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-6">
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <CardHeader className="bg-white border-b border-slate-100 pb-4">
                    <CardTitle className="flex items-center text-lg font-semibold text-slate-900">
                      <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3">
                        <UserCircle className="h-5 w-5" />
                      </div>
                      Información del Perfil
                    </CardTitle>
                  </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Nombre
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <User className="h-5 w-5 text-slate-400 mr-3" />
                    <span className="font-medium text-slate-900">
                      {user.first_name} {user.last_name}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    Correo Electrónico
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Mail className="h-5 w-5 text-slate-400 mr-3" />
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900">
                        {user.email}
                      </span>
                      <span className="text-xs text-slate-500">Principal</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Rol
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Briefcase className="h-5 w-5 text-slate-400 mr-3" />
                    <div className="flex flex-col">
                      <Badge
                        variant="secondary"
                        className="w-fit bg-slate-200 text-slate-700 hover:bg-slate-300"
                      >
                        {user.role?.name || "Usuario"}
                      </Badge>
                      {user.is_owner && (
                        <span className="text-xs text-slate-500 mt-1">
                          Propietario de la cuenta
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    Teléfono
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Phone className="h-5 w-5 text-slate-400 mr-3" />
                    <span className="font-medium text-slate-900">
                      {user.phone || "No especificado"}
                    </span>
                  </div>
                </div>
              </div>

              {company && (
                <>
                  <Separator className="my-8 bg-slate-100" />
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                      <div className="p-2 bg-purple-100 text-purple-600 rounded-lg mr-3">
                        <Building className="h-5 w-5" />
                      </div>
                      Información de la Empresa
                    </h3>
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Nombre</p>
                          <p className="font-semibold text-slate-900 text-lg">
                            {company.name}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            {company.email}
                          </p>
                        </div>
                        <div className="md:text-right">
                          <p className="text-sm text-slate-500 mb-1">
                            Identificador
                          </p>
                          <code className="text-sm bg-white px-3 py-1 rounded-lg border border-slate-200 text-slate-700 font-mono">
                            {company.slug}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="empresa" className="mt-0 border-none p-0 outline-none animate-in fade-in-50 duration-500">
            <OrganizationSettings />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              tu cuenta y eliminará tus datos de nuestros servidores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar Cuenta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
